import * as yup from 'yup';
import { FormFieldType, SurveyForm } from '../../extViewmodels';
import { computeVisibleFields } from '../containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { buildFileYupSchema } from '../containers/SurveyResponses/shared/FileUpload/utils';

/**
 * Builds a yup validation schema for custom survey properties.
 *
 * Handles all field types including FileUpload (array validation)
 * and standard text-based fields (string validation).
 *
 * When `surveyForm` is provided, required-checks for fields that are hidden by
 * conditional visibility rules are skipped at validation time (evaluated against
 * sibling answer values).
 *
 * Used by Event Registration, Donation, and other survey response forms.
 */
export const buildCustomPropertiesSchema = (
	fields: { id: string; isHidden?: boolean; isOptional?: boolean; label: string; fieldType?: string }[],
	surveyForm?: SurveyForm
) => {
	// Set of fieldIds represented in surveyForm.surveyParts. Used to distinguish
	// "field is governed by surveyForm and not currently visible" (skip required check)
	// from "field is not represented in surveyForm at all" (fall back to standard required).
	// The latter happens when surveyForm is stale or partial — e.g., a field was added
	// to customForm before surveyForm was repopulated.
	const surveyPartFieldIds = surveyForm?.surveyParts?.length
		? new Set(
				surveyForm.surveyParts.map(part => (part as { fieldId?: string }).fieldId).filter((id): id is string => !!id)
			)
		: undefined;

	return yup.object().shape(
		fields.reduce(
			(schema, field) => {
				if (field.isHidden) return schema;
				if (field.fieldType === FormFieldType.FileUpload) {
					schema[field.id] = buildFileYupSchema(field as any);
					return schema;
				}
				const errorMessage = `${field.label} is required`;
				if (field.isOptional) {
					schema[field.id] = yup.string().optional();
				} else if (surveyPartFieldIds?.has(field.id)) {
					schema[field.id] = yup.string().test('required-if-visible', errorMessage, function (value) {
						const visibleIds = computeVisibleFields(surveyForm, this.parent);
						if (!visibleIds.has(field.id)) return true;
						return !!value && value.trim() !== '';
					});
				} else {
					schema[field.id] = yup.string().required(errorMessage);
				}
				return schema;
			},
			{} as Record<string, any>
		)
	);
};
